import { z } from 'zod';
import { guessKindFromVendor, type ResourceKindGuess } from './tools.js';

/**
 * Network discovery — what a MikroTik's own ARP table, DHCP leases and
 * neighbour (LLDP/CDP/MNDP) table say is connected to it. This is what the
 * router itself sees, so it finds devices on VLANs and wireless clients that
 * a sweep from this server's own NIC never would (that sweep only sees
 * whatever segment the server itself sits on).
 *
 * The first pass is passive — three tables the router already maintains, one
 * SSH round trip. Devices it can't classify from that are then probed one at
 * a time *from the router* (see device-probe.ts), because this server usually
 * has no route to them, and each result streams in as it lands.
 */
export interface DiscoveredDevice {
  ip: string;
  mac: string | null;
  hostname: string | null;
  /** From the MAC's OUI alone — see `macVendor`. Null when the prefix isn't in the table or the address is randomised (see `macRandomized`). */
  vendor: string | null;
  /** Router interface the entry was learned on — null for a DHCP-only hit. */
  iface: string | null;
  via: 'arp' | 'dhcp';
  /** Set when a resource already matches this device, by MAC or address. */
  existingResourceId: string | null;
  /**
   * The address the matching resource had before this scan moved it to `ip`.
   * Set only when the match was by MAC, the device is online, and the
   * resource's recorded address was a different IP literal — i.e. DHCP (or
   * someone) gave a known device a new address, and the scan followed it.
   */
  previousAddress: string | null;
  /** The best guess at what this is — see `classifyDevice`. Null means nothing identified it, not "a computer". */
  suggestedKind: ResourceKindGuess | null;
  /**
   * Whether the router currently considers this device live — the `C`
   * (complete) flag on its ARP entry, or a DHCP lease that's actually bound
   * rather than just remembered. Best-effort from the same scan, not a fresh
   * ping.
   */
  online: boolean;
  /**
   * True when `mac` is a locally-administered (randomised) address — every
   * modern phone/laptop generates a fresh one per Wi-Fi network by default,
   * for privacy. `vendor` is null for these, permanently and correctly: the
   * address was never assigned to a manufacturer, so there is nothing an
   * OUI table could ever look up.
   */
  macRandomized: boolean;
  /**
   * What the device said about itself — an LLDP/CDP description, its DHCP
   * vendor-class, or an HTTP page title fetched from the router. Null when
   * nothing identified it; a MAC never carries a model, only a vendor.
   */
  model: string | null;
  modelSource: 'neighbor' | 'dhcp' | 'http' | null;
  /** The evidence `suggestedKind` rests on, in words — shown next to the type so a wrong guess can be traced. */
  reason: string | null;
}

/**
 * Everything discovery can learn about one device, gathered from different
 * places and fed to `classifyDevice` together.
 */
export interface DeviceEvidence {
  vendor: string | null;
  mac: string | null;
  /** The address is locally administered (see `DiscoveredDevice.macRandomized`). */
  macRandomized: boolean;
  hostname: string | null;
  /** DHCP option 60 as the router recorded it on the lease. */
  classId: string | null;
  /** The router's neighbour-table entry for this MAC, if the device speaks LLDP/CDP/MNDP. */
  neighbor: {
    identity: string | null;
    platform: string | null;
    board: string | null;
    version: string | null;
    description: string | null;
    /** LLDP system capabilities, lower-cased: telephone, wlan-access-point, router, bridge, … */
    caps: string[];
  } | null;
  /** An HTTP page title (or first visible text) fetched from the router. */
  banner: string | null;
  /** The name of a resource already registered for this device — operators name things "Core switch", "Reception printer". */
  knownName?: string | null;
}

export interface Classification {
  kind: ResourceKindGuess | null;
  vendor: string | null;
  model: string | null;
  modelSource: DiscoveredDevice['modelSource'];
  reason: string | null;
}

/**
 * The resource kinds discovery may file a device under, by the short key a
 * client sends back when adding it. A whitelist rather than trusting a
 * client-supplied plugin id: the add step looks the key up here.
 */
export const DISCOVERY_KINDS: Record<string, ResourceKindGuess> = {
  router: { pluginId: 'routers', kind: 'router', label: 'Router' },
  firewall: { pluginId: 'firewalls', kind: 'firewall', label: 'Firewall' },
  switch: { pluginId: 'networks', kind: 'switch', label: 'Switch' },
  'access-point': { pluginId: 'networks', kind: 'access-point', label: 'Wireless access point' },
  printer: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  'ip-camera': { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  'dvr-nvr': { pluginId: 'office-devices', kind: 'dvr-nvr', label: 'DVR/NVR' },
  'iot-device': { pluginId: 'office-devices', kind: 'iot-device', label: 'IoT device' },
  'ip-phone': { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  'voip-server': { pluginId: 'voip', kind: 'voip-server', label: 'VoIP server' },
  'linux-server': { pluginId: 'servers', kind: 'linux-server', label: 'Linux server' },
  'windows-server': { pluginId: 'servers', kind: 'windows-server', label: 'Windows server' },
  hypervisor: { pluginId: 'servers', kind: 'hypervisor', label: 'Hypervisor' },
  workstation: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  modem: { pluginId: 'networks', kind: 'modem', label: 'Modem / CPE' },
  // Filed as a computer when added — there's no phone resource kind.
  mobile: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
};

/** What a device says about itself, in the order a match is worth trusting: specific product words first. */
const TEXT_RULES: Array<{ re: RegExp; kind: keyof typeof DISCOVERY_KINDS }> = [
  { re: /\b(nvr|dvr)\b|video recorder|dvrdvs/i, kind: 'dvr-nvr' },
  { re: /ip ?cam|network cam|\bipc\b|hikvision|dahua|reolink|uniview|amcrest|netsurveillance|xmeye|ds-2cd|onvif/i, kind: 'ip-camera' },
  {
    re: /laserjet|officejet|deskjet|jetdirect|\bmfp\b|printer|ecosys|taskalfa|imagerunner|bizhub|kyocera|lexmark|ricoh|xerox|brother|epson|canon/i,
    kind: 'printer',
  },
  { re: /yealink|fanvil|grandstream|\bgxp\d|polycom|snom|htek|cisco spa|sip[- ]?t\d|ip ?phone|voip phone|sip phone|telephone/i, kind: 'ip-phone' },
  { re: /freepbx|asterisk|issabel|3cx/i, kind: 'voip-server' },
  { re: /access point|wireless ap\b|unifi ap|fortiap/i, kind: 'access-point' },
  { re: /routeros|mikrotik/i, kind: 'router' },
  { re: /catalyst|procurve|\bswitch\b/i, kind: 'switch' },
  { re: /diskstation|synology|qnap/i, kind: 'linux-server' },
  { re: /vmware esxi|\besxi\b/i, kind: 'hypervisor' },
  { re: /windows server/i, kind: 'windows-server' },
];

/**
 * Hostname shapes that mean one thing, and are safe to match on the name
 * alone. Hostnames are free text so this list is deliberately short — each
 * entry is a default a manufacturer ships, or a pattern confirmed against
 * real devices on the estate, not a keyword guess.
 */
const HOSTNAME_RULES: Array<{ re: RegExp; kind: keyof typeof DISCOVERY_KINDS }> = [
  // Default DHCP names: Brother (BRW/BRN + MAC tail), HP JetDirect (NPI + 6 hex).
  { re: /^(BRW|BRN)[0-9A-F]{6,12}$/i, kind: 'printer' },
  { re: /^NPI[0-9A-F]{6}$/i, kind: 'printer' },
  // Desk phones on this estate whose hostname is the model (FAP2714P), confirmed by the operator.
  { re: /^FAP\d{4}[A-Z]?$/, kind: 'ip-phone' },
];

/**
 * Prefixes the registry doesn't name (private assignments) but whose devices
 * were identified on the estate — the same family as a known one.
 */
const OUI_KIND_HINTS: Record<string, { kind: keyof typeof DISCOVERY_KINDS; why: string }> = {
  '30:43:D7': { kind: 'ip-phone', why: 'MAC prefix 30:43:D7 is the same desk-phone family as FAP2714P' },
};

/** Hostnames phones give themselves by default. */
const MOBILE_HOSTNAME = /(^|[-_ ])(iphone|ipad|galaxy|android|redmi|pixel)/i;

/**
 * A brand the device names itself, for when the registry only knows the
 * parent company. HP's networking gear is registered to "Hewlett Packard"
 * (or "Hewlett Packard Enterprise") but sold as Aruba/ProCurve/Instant On —
 * which is what an operator would call it.
 */
const BRAND_RULES: Array<{ re: RegExp; brand: string }> = [
  { re: /aruba|instant ?on|procurve|officeconnect/i, brand: 'Aruba (HPE)' },
  { re: /mikrotik|routeros/i, brand: 'MikroTik' },
  { re: /ubiquiti|unifi/i, brand: 'Ubiquiti' },
  { re: /yealink/i, brand: 'Yealink' },
  { re: /fanvil/i, brand: 'Fanvil' },
  { re: /grandstream/i, brand: 'Grandstream' },
  { re: /polycom/i, brand: 'Polycom' },
  { re: /hikvision/i, brand: 'Hikvision' },
  { re: /dahua/i, brand: 'Dahua' },
  { re: /kyocera|ecosys|taskalfa/i, brand: 'Kyocera' },
  { re: /laserjet|officejet|deskjet/i, brand: 'HP' },
];

/** Registry vendors that name a parent company, where the device's own wording is the better label. */
const PARENT_VENDOR = /^(HP|Hewlett Packard( Enterprise)?)$/i;

const GENERIC_CLASS_ID = /^(msft 5\.0|android-dhcp-?.*|dhcpcd.*|udhcp.*)$/i;

/** Names Windows gives itself out of the box — a computer, whatever else is said about it. */
const WORKSTATION_HOSTNAME = /^(DESKTOP|LAPTOP)-|^WIN-[0-9A-Z]{6,}$/i;

/**
 * Combines every signal discovery has into one best guess, and says which
 * signal it was.
 *
 * Order of trust: evidence that something is a computer (Windows' own DHCP
 * class and default hostnames — a phone never sends either), then what the
 * device announced over LLDP, then any text it gave about itself, then a
 * hostname that's a known default, and only last the MAC vendor — which for a
 * multi-product vendor (HP, Dell, Samsung) says who made it, not what it is.
 */
export function classifyDevice(e: DeviceEvidence): Classification {
  // Held on an object, not in loose `let`s: TypeScript doesn't see writes
  // made inside `pick` and would narrow a plain variable to `null` for good.
  const found: { key: string | null; reason: string | null } = { key: null, reason: null };
  const pick = (key: string, why: string) => {
    if (!found.key) {
      found.key = key;
      found.reason = why;
    }
  };

  if (e.classId && /^msft 5\.0$/i.test(e.classId)) pick('workstation', 'DHCP vendor-class "MSFT 5.0" (Windows)');
  if (e.hostname && WORKSTATION_HOSTNAME.test(e.hostname)) pick('workstation', `hostname "${e.hostname}" is a Windows default`);
  if (e.classId && /^android-dhcp/i.test(e.classId)) pick('mobile', `DHCP vendor-class "${e.classId}" (Android)`);
  if (e.hostname && MOBILE_HOSTNAME.test(e.hostname)) pick('mobile', `hostname "${e.hostname}" is a phone default`);

  const caps = e.neighbor?.caps ?? [];
  if (caps.includes('telephone')) pick('ip-phone', 'LLDP capability: telephone');
  else if (caps.includes('wlan-access-point')) pick('access-point', 'LLDP capability: wlan-access-point');

  const neighborText = e.neighbor
    ? [e.neighbor.description, e.neighbor.platform, e.neighbor.board, e.neighbor.identity].filter(Boolean).join(' ')
    : '';
  const classText = e.classId && !GENERIC_CLASS_ID.test(e.classId) ? e.classId : '';

  for (const [label, text] of [
    ['LLDP description', neighborText],
    ['web page title', e.banner ?? ''],
    ['saved resource name', e.knownName ?? ''],
    ['DHCP vendor-class', classText],
  ] as const) {
    if (!text || found.key) continue;
    for (const rule of TEXT_RULES) {
      const hit = rule.re.exec(text);
      if (hit) {
        pick(rule.kind, `${label} "${text.slice(0, 40)}" matches "${hit[0]}"`);
        break;
      }
    }
  }

  if (e.hostname) {
    const rule = HOSTNAME_RULES.find((r) => r.re.test(e.hostname!));
    if (rule) pick(rule.kind, `hostname "${e.hostname}" matches a known device pattern`);
  }
  // A router or bridge announcing itself over LLDP is a weaker claim than any
  // product text above — plenty of endpoints advertise a bare "bridge".
  if (caps.includes('router')) pick('router', 'LLDP capability: router');
  const hint = e.mac ? OUI_KIND_HINTS[e.mac.slice(0, 8).toUpperCase()] : undefined;
  if (hint) pick(hint.kind, hint.why);

  let kind: ResourceKindGuess | null = null;
  let reason = found.reason;
  if (found.key) kind = DISCOVERY_KINDS[found.key] ?? null;
  else {
    kind = guessKindFromVendor(e.vendor);
    if (kind) reason = `MAC vendor ${e.vendor}`;
    // Last resort, and honest about being one: a randomised address is what
    // phones (and some laptops) use for Wi-Fi privacy, and nothing else does.
    else if (e.macRandomized) {
      kind = DISCOVERY_KINDS['mobile'] ?? null;
      reason = 'randomised MAC — a phone or laptop on Wi-Fi privacy addresses';
    }
  }

  let model: string | null = null;
  let modelSource: DiscoveredDevice['modelSource'] = null;
  const neighborModel = e.neighbor?.description || [e.neighbor?.platform, e.neighbor?.board].filter(Boolean).join(' ') || null;
  if (neighborModel) {
    model = neighborModel;
    modelSource = 'neighbor';
  } else if (e.banner) {
    model = e.banner;
    modelSource = 'http';
  } else if (classText) {
    model = classText;
    modelSource = 'dhcp';
  }

  let vendor = e.vendor;
  if (!vendor || PARENT_VENDOR.test(vendor)) {
    const said = [neighborText, e.banner ?? '', classText, e.knownName ?? ''].join(' ');
    vendor = BRAND_RULES.find((r) => r.re.test(said))?.brand ?? vendor;
  }

  return { kind, vendor, model: model?.slice(0, 160) ?? null, modelSource, reason };
}

/**
 * Discovery is streamed NDJSON: the device list lands the moment the router's
 * tables are read, then each probed device updates its own row as it finishes.
 */
export type DiscoverEvent =
  | { type: 'devices'; devices: DiscoveredDevice[] }
  | { type: 'update'; key: string; patch: Partial<Pick<DiscoveredDevice, 'model' | 'modelSource' | 'suggestedKind' | 'reason' | 'vendor'>> }
  | { type: 'progress'; done: number; total: number }
  | { type: 'done' }
  | { type: 'error'; error: string };

/* ------------------------------------------------------------------ */
/* Automatic discovery                                                 */
/* ------------------------------------------------------------------ */

/**
 * The scheduled sweep (server: auto-discovery.ts): the same ARP + DHCP read
 * the discovery card does, run from every enabled MikroTik on a timer, with
 * each MAC's latest sighting kept and a resource whose MAC turned up at a
 * new address moved to follow it. Settings are stored in the database;
 * `null` fields fall back to the AUTO_DISCOVERY_ENABLED / DISCOVERY_INTERVAL_MIN
 * environment defaults.
 */
export const autoDiscoverySettingsUpdateSchema = z.object({
  enabled: z.boolean(),
  /** Minutes between sweeps. */
  intervalMin: z.number().int().min(5).max(1440).optional(),
  /** Move a resource's address when its MAC is seen elsewhere (per-resource opt-out: `config.autoUpdateAddress = false`). */
  updateAddresses: z.boolean().optional(),
});
export type AutoDiscoverySettingsUpdate = z.infer<typeof autoDiscoverySettingsUpdateSchema>;

export interface AutoDiscoveryRunSummary {
  startedAt: number;
  durationMs: number;
  routers: number;
  failed: number;
  devices: number;
  moved: number;
}

export interface AutoDiscoverySettings {
  enabled: boolean;
  intervalMin: number;
  updateAddresses: boolean;
  /** True while a sweep is in progress. */
  running: boolean;
  lastRunAt: number | null;
  lastRun: AutoDiscoveryRunSummary | null;
  lastError: string | null;
  updatedAt: number | null;
}

/** The latest place a MAC was seen, one row per MAC. */
export interface DiscoverySighting {
  mac: string;
  ip: string;
  hostname: string | null;
  vendor: string | null;
  /** The router whose tables showed it. */
  viaResourceId: string | null;
  viaResourceName: string | null;
  /** The resource registered with this MAC, if any. */
  resourceId: string | null;
  resourceName: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
  lastIpChangeAt: number | null;
}

/** A resource address discovery moved, by hand ("scan") or on the timer ("auto"). */
export interface DiscoveryAddressChange {
  id: number;
  resourceId: string;
  resourceName: string;
  mac: string | null;
  fromAddress: string | null;
  toAddress: string;
  viaResourceId: string | null;
  viaResourceName: string | null;
  source: 'auto' | 'scan';
  ts: number;
}

export interface DiscoveryRecent {
  sightings: DiscoverySighting[];
  changes: DiscoveryAddressChange[];
}
